import React, { useEffect, useState } from "react";
import { InputSearch } from "../../../../components/InputSearch";
import SendTxTemplate from "./SendTxTemplate";
import { useWallet } from "../../../../hooks/useWallet";
import { Button, ButtonSize } from "../../../../components/Button";
import { ButtonVariant } from "../../../../config/colors";
import ButtonsInfosTxSenderReceiver from "./ButtonsInfosTxSenderReceiver";
import { SendTransactionStateRecipient } from "../SendTransaction";
import { formatAmount, formatAmountWithSATS, formatNumberWithCommas, localeFormatNumber } from "../../../../utils/Utils";
import { ScanData } from "../../../../repositories/WalletRepository";

interface SendTxStep2Props {
  onConfirm: (amount: number) => void;
  defaultAmount?: number;
  recipient?: SendTransactionStateRecipient;
  scanData?: ScanData;
}

export const getAmountNumber = (amount: string) => {
    return Number(amount.replace(/[^0-9]/g, ''));
};

const SendTxStep2: React.FC<SendTxStep2Props> = ({ onConfirm, defaultAmount, recipient, scanData }) => {
    const [amount, setAmount] = useState(formatNumberWithCommas(defaultAmount?.toString() ?? ''));
    const [error, setError] = useState('');
    const {balance} = useWallet();
    
    const handleButtonClick = () => {
        onConfirm(getAmountNumber(amount));
    }

    useEffect(() => {
        if(getAmountNumber(amount) > (getMaxAmountSpendable()))
            setError('Insufficient balance');
        else
            setError('');
    }, [amount, balance]);

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Get raw input value
        const rawValue = e.target.value;
        // Remove any existing commas
        const cleanValue = rawValue.replace(/,/g, '');
        // Format with commas
        const formattedValue = formatNumberWithCommas(cleanValue);
        setAmount(formattedValue);
    };

    const getMaxAmountSpendable = () : number => {
        if(recipient?.bulkPayment)
            return recipient.bulkPayment.maxSendablePerRecipient ?? 0;
        else
            return scanData?.maxSendable ?? 0;
    }

    return (
        <SendTxTemplate
            onButtonClick={handleButtonClick}
            buttonDisabled={amount === '' || error !== ''}
            error={error}
        >
            <InputSearch
                placeholder="Amount"
                value={amount}
                onChange={handleAmountChange}
                onClear={() => setAmount('')}
                inputMode="numeric"
            />

            {balance && balance > 0 && (
                <div className="flex flex-row justify-center mt-4">
                    <Button
                        size={ButtonSize.small}
                        onClick={() => setAmount(formatNumberWithCommas(Math.floor(getMaxAmountSpendable()).toString() ?? ''))}
                        variant={ButtonVariant.unselected}
                        type="button"
                    >
                        Max {formatAmountWithSATS(Math.floor(getMaxAmountSpendable()) ?? 0)}
                    </Button>
                </div>
            )}

            {
                scanData?.lockedFunds && isFutureDate(scanData.lockedFunds.until) && (
                    <div className="flex flex-row justify-center mt-8">
                        <p className="text-brand-red text-center">
                            Your {formatAmount(scanData?.lockedFunds.amountMsat / 1000)} Sats Bonus will be available in {getHoursString(new Date(scanData?.lockedFunds.until ?? ''))}
                        </p>
                    </div>
                )
            }
            
            <div className="flex flex-row justify-center mt-8">
                <ButtonsInfosTxSenderReceiver
                    recipient={recipient}
                    clickable={false}
                />
            </div>
        </SendTxTemplate>
    );
}

function isFutureDate(date: string) {
    return new Date(date) > new Date();
}

const getHoursString = (date: Date): string => {
    const hours = Math.floor((date.getTime() - new Date().getTime()) / (1000 * 60 * 60));
    return `${hours} hour${hours !== 1 ? 's' : ''}`;
  };

export default SendTxStep2;